import { EPaper } from '../types/epaper';

// Function to convert your articles.json format to EPaper format
export const convertArticlesToEPapers = (articles: any[]): EPaper[] => {
  return articles.map((article, index) => {
    const timestamp = article['@timestamp'];
    const pdfUrl = article['@message'].trim();
    
    // Extract date from timestamp
    const date = new Date(timestamp).toISOString().split('T')[0];
    
    // Generate title from URL - decode URL-encoded characters
    const urlParts = pdfUrl.split('/');
    const filename = urlParts[urlParts.length - 1];
    const decodedFilename = decodeURIComponent(filename);
    const title = decodedFilename
      .replace('_compressed.pdf', '')
      .replace('.pdf', '')
      .replace(/_/g, ' ')
      .replace(/\+/g, ' ');
    
    // Generate thumbnail URL (assuming same structure as PDF)
    const thumbnailUrl = pdfUrl.replace('.pdf', '_thumb.jpg');
    
    // Enhanced special edition detection with category
    const titleLower = title.toLowerCase();
    let specialCategory: 'handbook' | 'review' | 'outlook' | 'special' | 'annual' | 'quarterly' | null = null;
    
    // Priority-based detection (order matters)
    if (titleLower.includes('handbook')) {
      specialCategory = 'handbook';
    } else if (titleLower.includes('review')) {
      specialCategory = 'review';
    } else if (titleLower.includes('outlook')) {
      specialCategory = 'outlook';
    } else if (titleLower.includes('special')) {
      specialCategory = 'special';
    } else if (titleLower.includes('annual')) {
      specialCategory = 'annual';
    } else if (titleLower.includes('quarterly')) {
      specialCategory = 'quarterly';
    }
    
    const isSpecialEdition = specialCategory !== null;
    
    return {
      id: index.toString(),
      title,
      date,
      pdfUrl,
      thumbnailUrl,
      timestamp,
      type: isSpecialEdition ? 'special' : 'regular',
      category: specialCategory
    };
  });
};

// Load ePapers from your articles.json file
export const loadEPapers = async (): Promise<EPaper[]> => {
  // Try to load from S3 bucket first
  console.log('Attempting to fetch from S3...');
  try {
    const response = await fetch('https://epaper.miningandenergy.com.na/articles.json', {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    if (response.ok) {
      const articles = await response.json();
      console.log(`Loaded ${articles.length} articles from S3 articles.json`);
      const epapers = convertArticlesToEPapers(articles);
      return epapers;
    }
  } catch (error) {
    console.log('S3 fetch failed, will try local file...');
  }
  
  // Fallback to local file for development
  console.log('Trying local file...');
  try {
    const localResponse = await fetch('/articles.json');
    if (!localResponse.ok) {
      throw new Error(`Failed to fetch local articles.json: ${localResponse.status}`);
    }
    const articles = await localResponse.json();
    console.log(`Loaded ${articles.length} articles from local articles.json`);
    
    console.log('First 3 articles:', articles.slice(0, 3));
    
    const epapers = convertArticlesToEPapers(articles);
    console.log(`Converted to ${epapers.length} ePapers`);
    console.log('First 3 ePapers:', epapers.slice(0, 3));
    
    return epapers;
  } catch (error) {
    console.error('Error loading ePapers:', error);
    console.error('Error details:', error instanceof Error ? error.message : String(error));
    throw new Error('Failed to load ePapers');
  }
};

// Alternative: Load from actual articles.json file
export const loadEPapersFromFile = async (): Promise<EPaper[]> => {
  try {
    const response = await fetch('https://epaper.miningandenergy.com.na/articles.json', {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    const articles = await response.json();
    const epapers = convertArticlesToEPapers(articles);
    return epapers;
  } catch (error) {
    console.error('Error loading ePapers from file:', error);
    throw new Error('Failed to load ePapers from file');
  }
};
